import { CSSObject } from "@emotion/css/create-instance";
import { FunctionComponent } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/hogast-gastropool-pay-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  ColorScheme,
  HogastGastropoolPayModuleSettings,
  Language,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
} from "../../utils/utils.js";
import IframeWidget from "../IframeWidget.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

type Props = BaseModuleProps<HogastGastropoolPayModuleSettings>;

const HogastGastropoolPayModule: FunctionComponent<Props> = ({
  translatedModule: {
    id,
    settings: { textAlign, key },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  translatedModule,
  isFirstOnPage,
  isActive,
  isPreview,
}) => {
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const baseUrl = getBaseUrl(key);
  const scriptUrl = getScriptUrl({ languageId, baseUrl, key });

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className={styles.HogastGastropoolPayModule}
      colors={{ background: scheme.main.separator, color: scheme.main.text }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>

      <div className={moduleBaseStyles.Module__Wrapper}>
        {!!key && (
          <IframeWidget
            isActive={isActive}
            isPreview={isPreview}
            scriptUrl={scriptUrl}
            style={getStyle(scheme)}
          >
            <div id="rxdeposit-container">
              <div id="hg-ti-form">
                <div className="block">
                  <img src={`${baseUrl}/app_themes/deposit/loader.gif`} />
                </div>
              </div>
            </div>
          </IframeWidget>
        )}

        {!key && (
          <div className={styles.HogastGastropoolPayModule__Empty}>
            hogast/gastropool Zahlknopf
          </div>
        )}
      </div>
    </ModuleWithHeadings>
  );
};

const getBaseUrl = (key: string) =>
  ({
    hogast: "https://secure.hogast.it",
    gastropool: "https://secure.gastropool.it",
  })[key.length === 128 ? "gastropool" : "hogast"];

const getScriptUrl = ({
  languageId,
  baseUrl,
  key,
}: {
  languageId: Language;
  baseUrl: string;
  key: string;
}) => {
  const lang = getSupportedLanguage(languageId, ["de", "it", "en"]);
  return `${baseUrl}/rxdeposit/widget/${lang}?key=${key}`;
};

const getStyle = (scheme: ColorScheme): CSSObject => ({
  "#hg-wi-form": {
    background: scheme.main.background,
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "3em 6%",

    ".h-wi-button": {
      color: scheme.primary.text,
      background: scheme.primary.background,
      borderRadius: 0,
    },
    ".h-wi-button:hover": { background: scheme.primary.background },
    ".h-wi-footer .h-wi-text": { color: scheme.main.text },
    ".h-wi-required-text": { color: scheme.main.text },
    ".h-wi-content .h-wi-row .h-wi-value input": {
      background: scheme.secondary.background,
      color: scheme.secondary.text,
      border: 0,
      padding: "0.65em 0.75em",
      fontSize: "1em",
      height: "auto",
    },
    ".h-wi-amounts": {
      background: scheme.secondary.background,
      color: scheme.secondary.text,
    },
    ".h-wi-content .h-wi-row": { background: "transparent", borderBottom: 0 },
    ".h-wi-content .h-wi-row.h-wi-bold-row": {
      background: "transparent",
      borderBottom: 0,
    },
    ".h-wi-content .h-wi-row:not(:first-child)": { marginTop: "1em" },
  },
});

export default HogastGastropoolPayModule;
